import { IconBrandShield, IconCheck, IconShieldOff } from '@myplayer/ui';
import { useState, type ReactElement } from 'react';
import type { AuditChainClientVerification } from './audit-chain-client.js';
import {
  ACTOR_FILTER_OPTIONS,
  AUDIT_SORT_OPTIONS,
  RESOURCE_FILTER_OPTIONS,
  formatAuditActionLabel,
  type AuditActorTypeFilter,
  type AuditEntryRecord,
  type AuditFacetCounts,
  type AuditResourceTypeFilter,
  type AuditSortOrder,
} from './audit-log-helpers.js';
import type { WorkspaceOption } from './auth-context.js';
import { Badge, Button, SearchFilterBar, Select } from './components/ui/primitives.js';

export * from './audit-log-columns.js';

export interface AuditFilterBarProps {
  readonly searchQuery: string;
  readonly onSearchChange: (value: string) => void;
  readonly resourceTypeFilter: AuditResourceTypeFilter;
  readonly onResourceTypeChange: (value: AuditResourceTypeFilter) => void;
  readonly actorTypeFilter: AuditActorTypeFilter;
  readonly onActorTypeChange: (value: AuditActorTypeFilter) => void;
  readonly actionFilter: string;
  readonly onActionChange: (value: string) => void;
  readonly distinctActions: readonly string[];
  readonly sortOrder?: AuditSortOrder | undefined;
  readonly onSortOrderChange?: ((value: AuditSortOrder) => void) | undefined;
  readonly viewMode?: 'table' | 'cards' | undefined;
  readonly onToggleViewMode?: ((nextMode: 'table' | 'cards') => void) | undefined;
  readonly facetCounts?: AuditFacetCounts | undefined;
  readonly workspaceFilter?: string | undefined;
  readonly onWorkspaceFilterChange?: ((value: string) => void) | undefined;
  readonly availableWorkspaces?: readonly WorkspaceOption[] | undefined;
  readonly showWorkspaceFilter?: boolean | undefined;
  readonly defaultWorkspaceFilter?: string | undefined;
}

export function AuditChainStatusBadge({
  verification,
}: {
  readonly verification: AuditChainClientVerification;
}): ReactElement {
  if (!verification.valid) {
    return (
      <Badge
        variant="invalid"
        size="lg"
        data-testid="audit-chain-status-badge"
        data-chain-status="TAMPERED"
      >
        <IconShieldOff />
        <span>Chain Integrity Broken</span>
      </Badge>
    );
  }
  if (verification.status === 'UNCHAINED') {
    const unverifiedTotal = verification.unverifiedCount ?? verification.checkedCount;
    return (
      <Badge
        variant="secondary"
        size="lg"
        data-testid="audit-chain-status-badge"
        data-chain-status="UNCHAINED"
        title="Legacy audit log entries without SHA-256 chain hashes"
      >
        <IconBrandShield />
        <span>
          Unverified ({unverifiedTotal} {unverifiedTotal === 1 ? 'entry' : 'entries'})
        </span>
      </Badge>
    );
  }
  return (
    <Badge
      variant="valid"
      size="lg"
      data-testid="audit-chain-status-badge"
      data-chain-status={verification.status}
      title="SHA-256 hash chain verified across audit log entries"
    >
      <IconBrandShield />
      <span>
        Chain Verified ({verification.checkedCount}{' '}
        {verification.checkedCount === 1 ? 'entry' : 'entries'}
        {verification.unverifiedCount ? `, ${String(verification.unverifiedCount)} unverified` : ''}
        )
      </span>
    </Badge>
  );
}

export function AuditChainCell({ entry }: { readonly entry: AuditEntryRecord }): ReactElement {
  const [copied, setCopied] = useState(false);
  if (!entry.sequence && !entry.entryHash && !entry.prevHash) {
    return (
      <Badge
        variant="outline"
        size="sm"
        data-testid={`audit-sequence-${entry.auditId}`}
        data-chain-entry-status="unverified"
      >
        Unverified
      </Badge>
    );
  }
  const shortEntryHash =
    entry.entryHash && entry.entryHash.length > 10
      ? `${entry.entryHash.slice(0, 10)}…`
      : (entry.entryHash ?? '—');
  const shortPrevHash =
    entry.prevHash === 'GENESIS'
      ? 'GENESIS'
      : entry.prevHash && entry.prevHash.length > 8
        ? `${entry.prevHash.slice(0, 8)}…`
        : (entry.prevHash ?? '—');
  const handleCopyHash = (): void => {
    if (!entry.entryHash) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(entry.entryHash);
    }
    setCopied(true);
  };

  return (
    <div className="flex flex-col gap-1 overflow-hidden font-mono text-[11px]">
      <div className="flex items-center gap-1.5">
        <Badge variant="secondary" size="sm" data-testid={`audit-sequence-${entry.auditId}`}>
          #{String(entry.sequence ?? '?')}
        </Badge>
        {entry.entryHash !== undefined && (
          <Button
            type="button"
            variant="secondary"
            size="xs"
            data-testid={`copy-audit-hash-${entry.auditId}`}
            aria-label={`Copy entry hash for ${entry.auditId}`}
            title={`Full SHA-256 entryHash: ${entry.entryHash}`}
            onClick={handleCopyHash}
          >
            <span data-testid={`audit-entry-hash-${entry.auditId}`}>{shortEntryHash}</span>
            {copied ? (
              <span className="inline-flex items-center gap-0.5 font-sans font-semibold text-status-success">
                <IconCheck /> Copied
              </span>
            ) : (
              <span className="font-sans text-[10px] text-fg-secondary">Copy</span>
            )}
          </Button>
        )}
      </div>
      <span
        data-testid={`audit-prev-hash-${entry.auditId}`}
        className="truncate text-[11px] text-fg-muted"
      >
        prev: {shortPrevHash}
      </span>
    </div>
  );
}

export function AuditFilterBar(props: AuditFilterBarProps): ReactElement {
  const showWs = props.showWorkspaceFilter ?? false;
  const defaultWs = props.defaultWorkspaceFilter ?? 'ALL';
  const hasActiveFacetFilters =
    props.resourceTypeFilter !== 'ALL' ||
    props.actorTypeFilter !== 'ALL' ||
    props.actionFilter !== 'ALL' ||
    (showWs && props.workspaceFilter !== undefined && props.workspaceFilter !== defaultWs);
  const hasActiveFilters = props.searchQuery.trim().length > 0 || hasActiveFacetFilters;
  const handleClearFilters = (): void => {
    props.onSearchChange('');
    props.onResourceTypeChange('ALL');
    props.onActorTypeChange('ALL');
    props.onActionChange('ALL');
    if (showWs) props.onWorkspaceFilterChange?.(defaultWs);
  };

  return (
    <SearchFilterBar
      data-testid="audit-search-bar"
      aria-label="Audit log search and filters"
      searchId="search-audit-log"
      searchAriaLabel="Search audit log"
      searchPlaceholder="Search actor, resource ID, action, sequence, hash..."
      clearSearchAriaLabel="Clear audit search"
      searchQuery={props.searchQuery}
      onSearchChange={props.onSearchChange}
      sortId="sort-audit-log"
      sortAriaLabel="Sort audit log"
      sortValue={props.sortOrder ?? 'NEWEST'}
      sortOptions={AUDIT_SORT_OPTIONS}
      onSortChange={(val) => props.onSortOrderChange?.(val as AuditSortOrder)}
      viewMode={props.viewMode}
      onToggleViewMode={props.onToggleViewMode}
      hasActiveFilters={hasActiveFilters}
      hasActiveFacetFilters={hasActiveFacetFilters}
      onClearFilters={handleClearFilters}
      clearButtonTestId="clear-audit-filters-btn"
    >
      {showWs && props.availableWorkspaces && props.onWorkspaceFilterChange && (
        <Select
          id="filter-audit-workspace"
          aria-label="Filter by workspace"
          filterLabel="Workspace"
          data-testid="audit-workspace-filter"
          className="min-w-[140px]"
          value={props.workspaceFilter ?? 'ALL'}
          optionCounts={props.facetCounts?.workspaceCounts}
          onChange={(e) => props.onWorkspaceFilterChange?.(e.target.value)}
        >
          <option value="ALL">All workspaces (Organisation)</option>
          {props.availableWorkspaces.map((ws) => (
            <option key={ws.workspaceId} value={ws.workspaceId}>
              {ws.name} ({ws.workspaceId})
            </option>
          ))}
        </Select>
      )}
      <Select
        id="filter-audit-resource-type"
        aria-label="Filter by resource type"
        filterLabel="Resource"
        className="min-w-[140px]"
        value={props.resourceTypeFilter}
        optionCounts={props.facetCounts?.resourceCounts}
        onChange={(e) => props.onResourceTypeChange(e.target.value as AuditResourceTypeFilter)}
      >
        {RESOURCE_FILTER_OPTIONS.map(([val, label]) => (
          <option key={val} value={val}>
            {label}
          </option>
        ))}
      </Select>
      <Select
        id="filter-audit-actor-type"
        aria-label="Filter by actor type"
        filterLabel="Actor"
        className="min-w-[130px]"
        value={props.actorTypeFilter}
        optionCounts={props.facetCounts?.actorCounts}
        onChange={(e) => props.onActorTypeChange(e.target.value as AuditActorTypeFilter)}
      >
        {ACTOR_FILTER_OPTIONS.map(([val, label]) => (
          <option key={val} value={val}>
            {label}
          </option>
        ))}
      </Select>
      <Select
        id="filter-audit-action"
        aria-label="Filter by action"
        filterLabel="Action"
        className="min-w-[140px]"
        value={props.actionFilter}
        optionCounts={props.facetCounts?.actionCounts}
        onChange={(e) => props.onActionChange(e.target.value)}
      >
        <option value="ALL">All actions</option>
        {props.distinctActions.map((act) => (
          <option key={act} value={act}>
            {formatAuditActionLabel(act)} ({act})
          </option>
        ))}
      </Select>
    </SearchFilterBar>
  );
}
